import React, { useState, useRef, FormEvent } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import arrowOutlined from "@/assets/arrow-outlined.svg";
import { Url } from "@/helpers/url";
import { useServices } from "@/services-context";
import styles from "./forgot-password.module.scss";
import fonts from "@/style/fonts.module.scss";
import EnterHandler from "@/elements-react/enter-handler";
import StoreHeader from "@/elements-react/store-header";
import { FooterElement } from "@/elements-react/footer-element/FooterElement";
import { FixLink } from "@/elements-react/fix-link";
import { FixButton } from "@/elements-react/fix-button";
import { Input } from "@/uikit";
import { useLayoutType, LayoutTypes } from "@/util-react/layout";
import { Icon } from "@/elements-react/fix-icon";
import { pageSuspense, useSuspenseLoader } from "@/helpers/suspense";
import { Services } from "@/services/services";
import { LocationHook, RouteHook, useLocation, useRoute } from "preact-iso";
import { CUSTOMER_CHANNEL_LISTENER_NAME } from "@/services/customer-channel.ts";
import { googleEvent } from "@/helpers/google-events.ts";
import {Header} from "@/elements-react/catalog-layout/header";

const fetchPageData = async (params: { services: Services, location: LocationHook, route: RouteHook }) => {
  const { services, location, route } = params;

  await services.initialize.prepare({
    location: location,
    route: route,
  });
};

const ForgotPasswordPage = pageSuspense(() => {
  const route = useRoute();
  const location = useLocation();
  const services = useServices([CUSTOMER_CHANNEL_LISTENER_NAME]);

  useSuspenseLoader({
    key: "forgot-route",
    loader: () => fetchPageData({
      services: services,
      location: location,
      route: route
    })
  });

  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState("");
  const [isSent, setIsSent] = useState(false);
  const [email, setEmail] = useState("");
  const formRef = useRef<HTMLFormElement>(null);
  const { layoutType, defaultLayoutStyleType } = useLayoutType();
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.currentTarget.value);
    setForgotPasswordError("");
  };

  const handleClear = () => {
    setEmail("");
    setForgotPasswordError("");
  };

  const handleSubmit = async (e?: FormEvent) => {
    if (e) e.preventDefault();

    if (!email) {
      return;
    }

    // Проверка валидности email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setForgotPasswordError(t("x926694e5jfby7au.email-incorrect", "Please enter a valid email address"));
      return;
    }

    setForgotPasswordError("");
    setLoading(true);

    try {
      const result = await services.customerChannel.requestRecoveryPassword({ email });
      setLoading(false);

      if (!result.success) {
        setForgotPasswordError(result.errors?.[0]?.message ?? t("vy0rzkl108ag908k.incorrect-username", "Incorrect Username"));
        return;
      }

      setIsSent(true);
    } catch (error) {
      setLoading(false);
      setForgotPasswordError(t("vy0rzkl108ag908k.incorrect-username", "Incorrect Username"));
    }
  };

  const renderSuccessMessage = () => (
    <div className={styles.successMessage}>
      <p>
        {t("8054911heo3kzbv3.success-message", "We've sent password reset instructions to")} <span className={styles.emailNotice}>{email}</span>
      </p>
      <p className={styles.info}>{t("m4gwrm5tfi4bxg40.email-info", "This email may take a few minutes to arrive in your inbox.")}</p>
    </div>
  );

  const renderInputs = () => (
    <>
      <div className={styles.inputsContainer}>
        <Input
          name="email"
          type="email"
          placeholder={t("stci3dd82scs5j9p.your-email", "* Your email")}
          helpText={t("x926694e5jfby7au.email-incorrect", "Please enter a valid email address")}
          required={true}
          clearable={true}
          customError={forgotPasswordError}
          value={email}
          onInput={handleChange}
          onClear={handleClear}
        />
      </div>
      <FixButton onClick={handleSubmit} variant="primary" type="submit" fullWidth={true} loading={loading} className={styles.forgotPasswordBtn}>
        {t("su819ybsyqbui0hi.reset-password", "Reset password")}
      </FixButton>
    </>
  );

  return (
    <div className={classNames(styles.root)}>
      <Header simple={true}></Header>

      <div className={classNames(styles[defaultLayoutStyleType])}>
        <div className={styles.forgotPasswordPageContainer}>
          {layoutType === LayoutTypes.TYPE_MOBILE && <span className={classNames(styles.continueShoppingContainer)}>{renderContinueShoppingLink()}</span>}

          <div className={styles.forgotPasswordContainer}>
            <EnterHandler onEnter={handleSubmit}>
              <form className={styles.forgotPasswordForm} name="forgotPassword" ref={formRef} onSubmit={handleSubmit}>
                <div>
                  <h1 className={classNames(styles.forgotPasswordHeading, fonts["font-lg-normal"])}>
                    {t("s0hdh7oc8j01ydvf.forgot-password-text", "Forgot your password?")}
                  </h1>
                </div>
                {isSent ? renderSuccessMessage() : renderInputs()}
              </form>
            </EnterHandler>
          </div>
        </div>
      </div>

      {layoutType === LayoutTypes.TYPE_DESKTOP && <FooterElement />}
    </div>
  );
});

export { ForgotPasswordPage };
